import React from 'react';
import { Zap } from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const RevenueSourcesChart = ({ monthlyData }) => {
    // Handle empty or invalid data
    const hasData = monthlyData && monthlyData.length > 0 && monthlyData.some(item => item.revenue > 0);

    if (!hasData) {
        return (
            <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 dark:group-hover:bg-primary/20 transition-colors">
                            <Zap className="h-4 w-4 text-primary/50 group-hover:text-primary/80 dark:group-hover:text-primary/90 transition-colors" />
                        </div>
                        <span className="text-foreground/90 dark:text-foreground/80 font-medium">Revenue Sources</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[300px] flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                            <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p className="text-sm">No revenue data available</p>
                            <p className="text-xs text-muted-foreground/70 mt-1">Start generating revenue to see sources</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 dark:group-hover:bg-primary/20 transition-colors">
                        <Zap className="h-4 w-4 text-primary/50 group-hover:text-primary/80 dark:group-hover:text-primary/90 transition-colors" />
                    </div>
                    <span className="text-foreground/90 dark:text-foreground/80 font-medium">Revenue Sources</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyData}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                className="stroke-border/20 dark:stroke-border/10"
                                vertical={false}
                                stroke="hsl(var(--border))"
                            />
                            <XAxis
                                dataKey="name"
                                className="text-xs"
                                tick={{ fill: 'hsl(var(--muted-foreground) / 0.6)' }}
                                stroke="hsl(var(--border) / 0.1)"
                                tickLine={false}
                            />
                            <YAxis
                                className="text-xs"
                                tick={{ fill: 'hsl(var(--muted-foreground) / 0.6)' }}
                                stroke="hsl(var(--border) / 0.1)"
                                tickLine={false}
                                tickFormatter={(value) => `$${Math.round(value).toLocaleString()}`}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        const currentDate = monthlyData.find(d => d.name === label)?.date;
                                        const downloads = payload[0].value || 0;
                                        const subscriptions = payload[1].value || 0;
                                        const total = downloads + subscriptions;

                                        // Only show percentages if there's actual revenue
                                        const downloadsPercent = total > 0 ? Math.round((downloads / total) * 100) : 0;
                                        const subscriptionsPercent = total > 0 ? Math.round((subscriptions / total) * 100) : 0;

                                        return (
                                            <div className="rounded-lg border border-border/20 bg-card/95 backdrop-blur-sm p-3 shadow-lg">
                                                <p className="text-sm font-medium text-foreground/95 dark:text-foreground/90">{currentDate}</p>
                                                <div className="mt-2 space-y-2">
                                                    <div>
                                                        <div className="flex items-center justify-between gap-4">
                                                            <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Downloads</span>
                                                            <span className="text-sm font-medium text-foreground/95 dark:text-foreground/90">
                                                                ${Math.round(downloads).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        {total > 0 && (
                                                            <>
                                                                <div className="mt-1 h-1 w-full rounded-full bg-primary/10">
                                                                    <div
                                                                        className="h-full rounded-full bg-primary/70"
                                                                        style={{ width: `${downloadsPercent}%` }}
                                                                    />
                                                                </div>
                                                                <p className="mt-1 text-xs text-muted-foreground/80 dark:text-muted-foreground/70">{downloadsPercent}% of total</p>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center justify-between gap-4">
                                                            <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Subscriptions</span>
                                                            <span className="text-sm font-medium text-foreground/95 dark:text-foreground/90">
                                                                ${Math.round(subscriptions).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        {total > 0 && (
                                                            <>
                                                                <div className="mt-1 h-1 w-full rounded-full bg-primary/10">
                                                                    <div
                                                                        className="h-full rounded-full bg-primary/40"
                                                                        style={{ width: `${subscriptionsPercent}%` }}
                                                                    />
                                                                </div>
                                                                <p className="mt-1 text-xs text-muted-foreground/80 dark:text-muted-foreground/70">{subscriptionsPercent}% of total</p>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="pt-2 border-t border-border/10">
                                                        <div className="flex items-center justify-between gap-4">
                                                            <span className="text-xs text-muted-foreground/80 dark:text-muted-foreground/70">Total Revenue</span>
                                                            <span className="text-sm font-medium text-foreground/95 dark:text-foreground/90">
                                                                ${Math.round(total).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Bar
                                dataKey="downloadsRevenue"
                                name="Downloads"
                                fill="hsl(var(--primary))"
                                radius={[4, 4, 0, 0]}
                                className="hover:opacity-80 dark:hover:opacity-90 transition-opacity"
                            />
                            <Bar
                                dataKey="subscriptionsRevenue"
                                name="Subscriptions"
                                fill="hsl(var(--primary) / 0.4)"
                                radius={[4, 4, 0, 0]}
                                className="hover:opacity-80 dark:hover:opacity-90 transition-opacity"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default RevenueSourcesChart; 